import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  DatabaseFieldType,
  type DatabaseField,
  type DatabaseValue
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type DatabaseDraft,
  type DatabaseChange,
  type ValueChange
} from '@databases/utils/database/changesets';
import { isEmptyValue } from '@databases/utils/database/field-types';
import { getDefaultValue } from '@databases/utils/database/utils';
import React, { type FunctionComponent, useCallback, useEffect, useState } from 'react';

import DefaultValuePicker from './DefaultValuePicker';
import DefaultValueInput from './shared/DefaultValueInput';

type DefaultValueSettingProps = {
  contentId: string;
  database: DatabaseDraft;
  field: DatabaseField;
};

const DefaultValueSetting: FunctionComponent<DefaultValueSettingProps> = ({
  contentId,
  database,
  field
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const defaultValue = getDefaultValue(database, field);
  const [optimisticDefaultValue, setOptimisticDefaultValue] =
    useState<DatabaseValue | null>(null);
  const effectiveDefaultValue = optimisticDefaultValue ?? defaultValue;
  const effectiveDefaultValueIsEmpty = defaultStoreUtils.useEqualSelector((state) =>
    isEmptyValue({ database, field, value: effectiveDefaultValue }, state)
  );
  const hasValue =
    !effectiveDefaultValueIsEmpty &&
    (field.settings.defaultEnabled || optimisticDefaultValue !== null);

  useEffect(() => {
    setOptimisticDefaultValue(null);
  }, [field.id]);

  const handleAddChangeToDraft = useCallback(
    (change: DatabaseChange) => {
      dispatch(addChangeToDraft(contentId, change));
    },
    [contentId, dispatch]
  );

  const handleChange = useCallback(
    (value: DatabaseValue, change: ValueChange | null) => {
      setOptimisticDefaultValue(value);
      const valueIsEmpty = isEmptyValue({ database, field, value }, store.getState());

      if (!field.settings.defaultEnabled && !valueIsEmpty) {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_DEFAULT_ENABLED,
            fieldId: field.id,
            defaultEnabled: true
          })
        );
      }

      if (field.settings.defaultEnabled && valueIsEmpty) {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_DEFAULT_ENABLED,
            fieldId: field.id,
            defaultEnabled: false
          })
        );
      }

      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.BULK_SET_UNDEFINED_VALUES,
          fieldId: field.id,
          value,
          change
        })
      );
    },
    [contentId, database, dispatch, field, store]
  );

  return (
    <DefaultValueInput
      hasValue={hasValue}
      shouldForwardLabelClick={field.type !== DatabaseFieldType.DATE}
      testId="field-settings.default-value"
    >
      {(slotProps) => (
        <DefaultValuePicker
          contentId={contentId}
          field={field}
          hasValue={hasValue}
          value={effectiveDefaultValue}
          onChange={handleChange}
          addChangeToDraft={handleAddChangeToDraft}
          slotProps={slotProps}
        />
      )}
    </DefaultValueInput>
  );
};

export default DefaultValueSetting;
